// Biometrics Display Mapping Utility
// Provides display labels and metadata for biometrics parameters in the frontend

import { biometricCategories } from './biometricParameters';

/**
 * Creates mapping objects for biometrics parameters
 */
const createBiometricsDisplayMapping = () => {
  const keyToLabelMap = {};
  const keyToUnitsMap = {};
  const keyToCategoryMap = {};
  const keyToMetadataMap = {};
  const categoryToParametersMap = {};
  
  biometricCategories.forEach(category => {
    categoryToParametersMap[category.key] = [];
    
    category.parameters.forEach(param => {
      // Use the standardized key from the parameter definition
      const standardizedKey = param.key;
      
      keyToLabelMap[standardizedKey] = param.name;
      keyToUnitsMap[standardizedKey] = param.units || '';
      keyToCategoryMap[standardizedKey] = category.title;
      keyToMetadataMap[standardizedKey] = {
        ...param,
        categoryKey: category.key,
        categoryTitle: category.title
      };
      
      categoryToParametersMap[category.key].push(standardizedKey);
    });
  });
  
  return { 
    keyToLabelMap, 
    keyToUnitsMap, 
    keyToCategoryMap, 
    keyToMetadataMap, 
    categoryToParametersMap 
  };
};

const { 
  keyToLabelMap, 
  keyToUnitsMap, 
  keyToCategoryMap, 
  keyToMetadataMap, 
  categoryToParametersMap 
} = createBiometricsDisplayMapping();

/**
 * Gets the display label for a biometrics parameter
 * @param {string} parameterKey - Standardized biometrics parameter key
 * @returns {string} - Human-readable display label
 */
export const getBiometricsDisplayLabel = (parameterKey) => {
  return keyToLabelMap[parameterKey] || parameterKey;
};

/**
 * Gets the units for a biometrics parameter
 * @param {string} parameterKey - Standardized biometrics parameter key
 * @returns {string} - Units string
 */
export const getBiometricsUnits = (parameterKey) => {
  return keyToUnitsMap[parameterKey] || '';
};

/**
 * Gets the category for a biometrics parameter
 * @param {string} parameterKey - Standardized biometrics parameter key
 * @returns {string} - Category title
 */
export const getBiometricsCategory = (parameterKey) => {
  return keyToCategoryMap[parameterKey] || 'Unknown';
};

/**
 * Gets the full metadata for a biometrics parameter
 * @param {string} parameterKey - Standardized biometrics parameter key
 * @returns {Object|null} - Parameter metadata or null if not found
 */
export const getBiometricsMetadata = (parameterKey) => {
  return keyToMetadataMap[parameterKey] || null;
};

/**
 * Gets formatted display text with units for a biometrics parameter
 * @param {string} parameterKey - Standardized biometrics parameter key
 * @returns {string} - Formatted display text with units
 */
export const getBiometricsDisplayWithUnits = (parameterKey) => {
  const label = getBiometricsDisplayLabel(parameterKey);
  const units = getBiometricsUnits(parameterKey);
  return units ? `${label} (${units})` : label;
};

/**
 * Gets all biometrics parameters for a specific category
 * @param {string} categoryKey - Category key (e.g., 'cognition', 'strength')
 * @returns {Array<string>} - Array of standardized parameter keys for the category
 */
export const getBiometricsParametersByCategory = (categoryKey) => {
  return categoryToParametersMap[categoryKey] || [];
};

/**
 * Gets all available biometric categories
 * @returns {Array<Object>} - Array of category objects with key and title
 */
export const getAllBiometricsCategories = () => {
  return biometricCategories.map(cat => ({
    key: cat.key,
    title: cat.title,
    parameterCount: cat.parameters.length
  }));
};

/**
 * Checks if a parameter key is a standardized biometrics parameter
 * @param {string} parameterKey - Parameter key to check
 * @returns {boolean} - True if it's a standardized biometrics parameter
 */
export const isBiometricsParameter = (parameterKey) => {
  return typeof parameterKey === 'string' && 
         parameterKey.startsWith('biometrics_') && 
         keyToLabelMap.hasOwnProperty(parameterKey);
};

/**
 * Extracts the category key from a standardized biometrics parameter
 * @param {string} parameterKey - Standardized biometrics parameter key
 * @returns {string|null} - Category key or null if not a valid biometrics parameter
 */
export const extractBiometricsCategory = (parameterKey) => {
  if (!isBiometricsParameter(parameterKey)) {
    return null;
  }
  
  const parts = parameterKey.split('_');
  return parts[1]; // The category is the second part after 'biometrics_'
};

/**
 * Extracts the parameter name from a standardized biometrics parameter
 * @param {string} parameterKey - Standardized biometrics parameter key
 * @returns {string|null} - Parameter name or null if not a valid biometrics parameter
 */
export const extractBiometricsParameterName = (parameterKey) => {
  if (!isBiometricsParameter(parameterKey)) {
    return null;
  }
  
  const parts = parameterKey.split('_');
  return parts.slice(2).join('_'); // Everything after 'biometrics_category_'
};

/**
 * Transforms biometrics data for display in the flowsheet
 * @param {Object} biometricsData - Raw biometrics data with standardized keys
 * @returns {Array<Object>} - Array of display objects for flowsheet
 */
export const transformBiometricsForDisplay = (biometricsData) => {
  if (!biometricsData || typeof biometricsData !== 'object') {
    return [];
  }
  
  return Object.entries(biometricsData)
    .filter(([key]) => isBiometricsParameter(key))
    .map(([key, value]) => ({
      key,
      label: getBiometricsDisplayLabel(key),
      value,
      units: getBiometricsUnits(key),
      displayValue: getBiometricsUnits(key) ? `${value} ${getBiometricsUnits(key)}` : value,
      category: getBiometricsCategory(key),
      categoryKey: extractBiometricsCategory(key),
      parameterName: extractBiometricsParameterName(key)
    }));
};

/**
 * Groups biometrics data by category for display
 * @param {Object} biometricsData - Raw biometrics data with standardized keys
 * @returns {Object} - Object with categories as keys and parameter arrays as values
 */
export const groupBiometricsByCategory = (biometricsData) => {
  const displayData = transformBiometricsForDisplay(biometricsData);
  const grouped = {};
  
  displayData.forEach(item => {
    if (!grouped[item.categoryKey]) {
      grouped[item.categoryKey] = {
        title: item.category,
        parameters: []
      };
    }
    grouped[item.categoryKey].parameters.push(item);
  });
  
  return grouped;
};

/**
 * Gets biometrics parameters that match a search term
 * @param {string} searchTerm - Search term to match against labels
 * @returns {Array<Object>} - Array of matching parameter metadata
 */
export const searchBiometricsParameters = (searchTerm) => {
  if (!searchTerm || typeof searchTerm !== 'string') {
    return [];
  }
  
  const lowerSearchTerm = searchTerm.toLowerCase();
  const results = [];
  
  Object.entries(keyToMetadataMap).forEach(([key, metadata]) => {
    const label = metadata.name.toLowerCase();
    const category = metadata.categoryTitle.toLowerCase();
    
    if (label.includes(lowerSearchTerm) || category.includes(lowerSearchTerm)) {
      results.push({
        key,
        ...metadata,
        displayLabel: getBiometricsDisplayWithUnits(key)
      });
    }
  });
  
  return results;
};

/**
 * Validates biometrics parameter data
 * @param {string} parameterKey - Standardized biometrics parameter key
 * @param {any} value - Value to validate
 * @returns {Object} - Validation result with isValid and message
 */
export const validateBiometricsValue = (parameterKey, value) => {
  const metadata = getBiometricsMetadata(parameterKey);
  
  if (!metadata) {
    return { isValid: false, message: 'Unknown parameter' };
  }
  
  if (value === null || value === undefined || value === '') {
    return { isValid: true, message: 'Optional parameter' };
  }
  
  // Basic numeric validation for numeric types
  if (metadata.type === 'Cognitive Test' || metadata.type === 'Biometric') {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return { isValid: false, message: 'Must be a valid number' };
    }
    
    if (numValue < 0) {
      return { isValid: false, message: 'Must be a positive number' };
    }
  }
  
  return { isValid: true, message: 'Valid' };
};

// Export the biometric categories for external use
export { biometricCategories };
